import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Menu, X, LayoutDashboard, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_feasibility-hub-10/artifacts/nb8rpfwp_Ruam%20Architects%20Logo.jpg";

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isDark = location.pathname === '/' || location.pathname === '/login';

  const toolLinks = [
    { href: '/feasibility', label: 'Is This Possible?' },
    { href: '/cost-calculator', label: 'What Will It Cost?' },
    { href: '/brief-builder', label: 'Build Your Brief' },
    { href: '/fit-score', label: 'Am I Ready?' },
    { href: '/architect', label: 'Guided Consultation' },
  ];

  const siteLinks = [
    { href: '/projects', label: 'Projects' },
    { href: '/about', label: 'About' },
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const isAdmin = user && (user.role === 'admin' || user.role === 'architect');
  const isOnAdmin = location.pathname.startsWith('/admin');

  const linkClass = (href) => `text-sm font-body font-medium transition-colors hover:text-brand-accent ${
    location.pathname === href ? 'text-brand-accent' : isDark ? 'text-white/70' : 'text-brand-dark/70'
  }`;

  return (
    <nav
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        isDark ? 'bg-brand-dark/80 backdrop-blur-lg border-b border-white/10' : 'bg-white/80 backdrop-blur-lg border-b border-black/10'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-16 gap-3">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2.5 flex-shrink-0" data-testid="nav-logo">
          <img src={LOGO_URL} alt="Raum Architects" className="w-9 h-9 rounded-full object-cover ring-1 ring-white/20" />
          <span className={`font-heading text-xl font-semibold tracking-wider ${isDark ? 'text-white' : 'text-brand-dark'}`}>
            RAUM
          </span>
        </Link>

        {/* Desktop Links — only ≥1024px; below that the hamburger takes over so
            the 7+ items never overlap or wrap. */}
        <div className="hidden lg:flex items-center gap-1">
          {/* Tools */}
          <div className="flex items-center gap-4 mr-3">
            {toolLinks.map((link) => (
              <Link key={link.href} to={link.href} data-testid={`nav-link-${link.href.slice(1)}`} className={linkClass(link.href)}>
                {link.label}
              </Link>
            ))}
          </div>

          {/* Separator + Site pages */}
          <div className={`w-px h-4 mx-2 ${isDark ? 'bg-white/15' : 'bg-black/10'}`} />
          <div className="flex items-center gap-4 mx-2">
            {siteLinks.map((link) => (
              <Link key={link.href} to={link.href} data-testid={`nav-link-${link.href.slice(1)}`} className={linkClass(link.href)}>
                {link.label}
              </Link>
            ))}
          </div>

          {/* Separator + Studio */}
          {isAdmin && (
            <>
              <div className={`w-px h-5 mx-2 ${isDark ? 'bg-white/15' : 'bg-black/10'}`} />
              <Link
                to="/admin"
                data-testid="nav-link-studio"
                className={`text-sm font-body font-semibold flex items-center gap-1.5 transition-colors px-3 py-1.5 rounded-sm ${
                  isOnAdmin
                    ? 'bg-brand-accent/15 text-brand-accent'
                    : isDark ? 'text-white/70 hover:text-brand-accent' : 'text-brand-dark/70 hover:text-brand-accent'
                }`}
              >
                <LayoutDashboard className="w-3.5 h-3.5" />
                Studio
              </Link>
            </>
          )}

          {user ? (
            <Button
              data-testid="nav-logout-btn"
              onClick={handleLogout}
              variant="ghost"
              size="sm"
              className={`ml-2 text-sm ${isDark ? 'text-white/70 hover:text-white' : 'text-brand-dark/70 hover:text-brand-dark'}`}
            >
              <LogOut className="w-3.5 h-3.5 mr-1.5" />
              Logout
            </Button>
          ) : (
            <Link to="/login" data-testid="nav-login-link">
              <Button
                size="sm"
                className="ml-2 bg-brand-accent text-brand-dark hover:bg-brand-accent/90 rounded-sm font-body text-xs font-semibold uppercase tracking-wider"
              >
                Sign In
              </Button>
            </Link>
          )}
        </div>

        {/* Mobile / tablet hamburger — shown below 1024px (covers iPad portrait too). */}
        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden flex-shrink-0"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle navigation"
        >
          {mobileOpen ? (
            <X className={`w-5 h-5 ${isDark ? 'text-white' : 'text-brand-dark'}`} />
          ) : (
            <Menu className={`w-5 h-5 ${isDark ? 'text-white' : 'text-brand-dark'}`} />
          )}
        </button>
      </div>

      {/* Mobile / tablet menu — shown below 1024px. */}
      {mobileOpen && (
        <div className={`lg:hidden px-6 pb-6 space-y-3 ${isDark ? 'bg-brand-dark/95' : 'bg-white/95'}`}>
          <p className={`text-xs font-heading uppercase tracking-wider pt-2 ${isDark ? 'text-white/30' : 'text-brand-dark/30'}`}>Tools</p>
          {toolLinks.map((link) => (
            <Link key={link.href} to={link.href} data-testid={`mobile-nav-${link.href.slice(1)}`} onClick={() => setMobileOpen(false)}
              className={`block text-sm font-body py-1.5 ${isDark ? 'text-white/80' : 'text-brand-dark/80'}`}>{link.label}</Link>
          ))}
          <p className={`text-xs font-heading uppercase tracking-wider pt-3 ${isDark ? 'text-white/30' : 'text-brand-dark/30'}`}>Practice</p>
          {siteLinks.map((link) => (
            <Link key={link.href} to={link.href} data-testid={`mobile-nav-${link.href.slice(1)}`} onClick={() => setMobileOpen(false)}
              className={`block text-sm font-body py-1.5 ${isDark ? 'text-white/80' : 'text-brand-dark/80'}`}>{link.label}</Link>
          ))}
          {isAdmin && (
            <Link to="/admin" onClick={() => setMobileOpen(false)} className="block text-sm font-body font-semibold py-1.5 text-brand-accent">Studio</Link>
          )}
          {!user && (
            <Link to="/login" onClick={() => setMobileOpen(false)} className="block text-sm font-body py-1.5 text-brand-accent">Sign In</Link>
          )}
        </div>
      )}
    </nav>
  );
}
